import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import {
  ArrowLineUpIcon,
  Check,
  Envelope,
  PencilSimpleLine,
  Phone,
  Plus,
  Trash,
  Users,
  X
} from "@phosphor-icons/react";
import ProfilePicturePlaceholder from "assets/images/profile-placeholder.png";
import { PhoneInputField, Select, TextField } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { IMAGE_URL } from "config";
import { useEmployeesPaginated } from "features/hr";
import { useFileUpload } from "hooks";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateLeader, useDeleteLeader, useUpdateLeader } from "../api";

export const Leadership = ({ church, isDenominationChurch = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddLeaderForm, setShowAddLeaderForm] = useState(false);
  const [editingLeaderId, setEditingLeaderId] = useState(null);
  const [leaders, setLeaders] = useState(church?.leaders || []);

  const leaderCreateMutation = useCreateLeader();
  const leaderUpdateMutation = useUpdateLeader();
  const leaderDeleteMutation = useDeleteLeader();

  const { t } = useTranslation();

  const employeesPaginatedQuery = useEmployeesPaginated({
    churchId: church?.id,
    page: 1,
    pageSize: 10,
    isDenomination: false,
    denominationId: ""
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      senior_pastor_title: church?.leaders?.senior_pastor?.[0]?.title || "",
      senior_pastor_first_name:
        church?.leaders?.senior_pastor?.[0]?.first_name || "",
      senior_pastor_last_name:
        church?.leaders?.senior_pastor?.[0]?.last_name || "",
      senior_pastor_email: church?.leaders?.senior_pastor?.[0]?.email || "",
      senior_pastor_phone: church?.leaders?.senior_pastor?.[0]?.phone || "",
      senior_pastor_started_date:
        church?.leaders?.senior_pastor?.[0]?.started_date || "",
      senior_pastor_image: null
    }
  });

  // Form for new leader
  const {
    register: registerNewLeader,
    handleSubmit: handleSubmitNewLeader,
    formState: { errors: newLeaderErrors },
    reset: resetNewLeader,
    setValue: setNewLeaderValue,
    watch: watchNewLeader
  } = useForm({
    defaultValues: {
      leader_title: "",
      leader_full_name: "",
      leader_phone: "",
      leader_email: "",
      leader_image: null
    }
  });

  // Form for editing existing leader
  const {
    register: registerEditLeader,
    handleSubmit: handleSubmitEditLeader,
    formState: { errors: editLeaderErrors },
    reset: resetEditLeader,
    setValue: setEditLeaderValue,
    watch: watchEditLeader
  } = useForm({
    defaultValues: {
      edit_leader_title: "",
      edit_leader_full_name: "",
      edit_leader_phone: "",
      edit_leader_email: "",
      edit_leader_image: null
    }
  });

  // Title options for the dropdown
  const titleOptions = [
    { optionLabel: "Pastor", value: "Pastor" },
    { optionLabel: "Associate Pastor", value: "Associate Pastor" },
    { optionLabel: "Elder", value: "Elder" },
    { optionLabel: "Deacon", value: "Deacon" },
    { optionLabel: "Minister", value: "Minister" },
    { optionLabel: "Director", value: "Director" }
  ];

  // File upload hooks
  const [
    seniorPastorSelectedFile,
    openSeniorPastorFileDialog,
    resetSeniorPastorFile
  ] = useFileUpload({
    accept: "image/*"
  });

  const [newLeaderSelectedFile, openNewLeaderFileDialog, resetNewLeaderFile] =
    useFileUpload({
      accept: "image/*"
    });

  const [
    editLeaderSelectedFile,
    openEditLeaderFileDialog,
    resetEditLeaderFile
  ] = useFileUpload({
    accept: "image/*"
  });

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit - reset form to original values
      reset();
      resetSeniorPastorFile();
    }
    setIsEditMode(!isEditMode);
  };

  const onSubmit = values => {
    console.log("Leadership form values:", values);

    const formData = new FormData();

    // Map form fields to API parameters for senior pastor
    formData.append(
      "title",
      values.senior_pastor_title ||
        church?.leaders?.senior_pastor?.[0]?.title ||
        ""
    );
    formData.append(
      "first_name",
      values.senior_pastor_first_name ||
        church?.leaders?.senior_pastor?.[0]?.first_name ||
        ""
    );
    formData.append("last_name", values.senior_pastor_last_name || "");
    formData.append(
      "email",
      values.senior_pastor_email ||
        church?.leaders?.senior_pastor?.[0]?.email ||
        ""
    );
    formData.append(
      "phone_number",
      values.senior_pastor_phone ||
        church?.leaders?.senior_pastor?.[0]?.phone ||
        ""
    );
    formData.append(
      "started_date",
      values.senior_pastor_started_date ||
        church?.leaders?.senior_pastor?.[0]?.started_date ||
        ""
    );
    formData.append("type", "senior_pastor");

    // If there's a selected image for senior pastor, add it to FormData
    if (seniorPastorSelectedFile) {
      formData.append("profile_picture", seniorPastorSelectedFile);
    }

    if (church?.leaders?.senior_pastor?.[0]?.id) {
      leaderUpdateMutation.mutate(
        {
          churchId: church?.id,
          leaderData: formData,
          leaderId: church?.leaders?.senior_pastor?.[0]?.id,
          isDenominationChurch: isDenominationChurch
        },
        {
          onSuccess: () => {
            setIsEditMode(false);
            resetSeniorPastorFile();
          }
        }
      );
    } else {
      leaderCreateMutation.mutate(
        {
          churchId: church?.id,
          leaderData: formData,
          isDenominationChurch: isDenominationChurch
        },
        {
          onSuccess: () => {
            setIsEditMode(false);
            resetSeniorPastorFile();
          }
        }
      );
    }
  };

  const handleAddLeader = () => {
    setShowAddLeaderForm(true);
  };

  const handleSaveLeader = values => {
    const formData = new FormData();

    // Split full name into first and last name
    const fullName = values.leader_full_name || "";
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    // Map form fields to API parameters for regular leader
    formData.append("title", values.leader_title || "");
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("email", values.leader_email || "");
    formData.append("phone_number", values.leader_phone || "");
    formData.append("started_date", ""); // Can add this field to the form if needed
    formData.append("type", "leader");

    // If there's a selected image for new leader, add it to FormData
    if (newLeaderSelectedFile) {
      formData.append("profile_picture", newLeaderSelectedFile);
    }

    // Create new leader using leader API
    leaderCreateMutation.mutate(
      {
        churchId: church?.id,
        leaderData: formData,
        isDenominationChurch: isDenominationChurch
      },
      {
        onSuccess: response => {
          // Add the new leader to the local state
          const newLeader = {
            id: response.data?.result?.id || Date.now(),
            title: values.leader_title,
            first_name: firstName,
            last_name: lastName,
            phone_number: values.leader_phone,
            email: values.leader_email,
            profile_picture: newLeaderSelectedFile
              ? URL.createObjectURL(newLeaderSelectedFile)
              : ProfilePicturePlaceholder
          };
          // Update leaders state properly
          setLeaders({
            ...leaders,
            leader: [...(leaders?.leader || []), newLeader]
          });
          // Keep the form visible for adding more leaders
          // setShowAddLeaderForm(false); // Removed this line
          resetNewLeader();
          resetNewLeaderFile();
        }
      }
    );
  };

  const handleCancelAddLeader = () => {
    setShowAddLeaderForm(false);
    resetNewLeader();
    resetNewLeaderFile();
  };

  const handleDeleteLeader = leaderId => {
    leaderDeleteMutation.mutate(
      {
        churchId: church?.id,
        leaderId: leaderId,
        isDenominationChurch: isDenominationChurch
      },
      {
        onSuccess: () => {
          // Remove the leader from local state
          setLeaders({
            ...leaders,
            leader: leaders?.leader?.filter(leader => leader.id !== leaderId)
          });
        }
      }
    );
  };

  const handleEditLeader = leader => {
    setEditingLeaderId(leader.id);
    // Pre-populate the edit form with current leader data
    resetEditLeader({
      edit_leader_title: leader.title || "",
      edit_leader_full_name: `${leader.first_name || ""} ${
        leader.last_name || ""
      }`.trim(),
      edit_leader_phone: leader.phone_number || "",
      edit_leader_email: leader.email || "",
      edit_leader_image: null
    });
  };

  const handleSaveEditLeader = values => {
    const formData = new FormData();

    // Split full name into first and last name
    const fullName = values.edit_leader_full_name || "";
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    // Map form fields to API parameters
    formData.append("title", values.edit_leader_title || "");
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("email", values.edit_leader_email || "");
    formData.append("phone_number", values.edit_leader_phone || "");
    formData.append("started_date", "");
    formData.append("type", "leader");

    // If there's a selected image, add it to FormData
    if (editLeaderSelectedFile) {
      formData.append("profile_picture", editLeaderSelectedFile);
    }

    // Update leader using leader API
    leaderUpdateMutation.mutate(
      {
        churchId: church?.id,
        leaderId: editingLeaderId,
        leaderData: formData,
        isDenominationChurch: isDenominationChurch
      },
      {
        onSuccess: () => {
          // Update the leader in local state
          setLeaders({
            ...leaders,
            leader: leaders?.leader?.map(leader =>
              leader.id === editingLeaderId
                ? {
                    ...leader,
                    title: values.edit_leader_title,
                    first_name: firstName,
                    last_name: lastName,
                    email: values.edit_leader_email,
                    phone_number: values.edit_leader_phone,
                    profile_picture: editLeaderSelectedFile
                      ? URL.createObjectURL(editLeaderSelectedFile)
                      : leader.profile_picture || ProfilePicturePlaceholder
                  }
                : leader
            )
          });
          setEditingLeaderId(null);
          resetEditLeader();
          resetEditLeaderFile();
        }
      }
    );
  };

  const handleCancelEditLeader = () => {
    setEditingLeaderId(null);
    resetEditLeader();
    resetEditLeaderFile();
  };

  const numberOfAssociatePastor = useMemo(() => {
    return (
      church?.leaders?.leader?.filter(
        leader => leader.title === "Associate Pastor"
      ).length || 0
    );
  }, [church?.leaders?.leader]);

  const numberOfStaffMembers = useMemo(() => {
    return employeesPaginatedQuery.data?.result?.total_employees || 0;
  }, [employeesPaginatedQuery.data]);

  const numberOfLeadershipTeam = useMemo(() => {
    return church?.leaders?.leader?.length || 0;
  }, [church?.leaders?.leader]);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        {!isDenominationChurch && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Users size={24} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("churches.profile.leadershipAndStaff")}
            </Typography>
          </Stack>
        )}

        {/* Stat cards grid */}
        {!isDenominationChurch && (
          <Stack direction="row" spacing={2}>
            {/* Senior Pastor Card */}
            {/* <Card
            sx={{
              flex: 1,
              backgroundColor: "#F8ECE3",
              border: "none",
              borderRadius: "16px",
              boxShadow: "none"
            }}
            elevation={0}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#FE964A",
                    fontSize: "2rem",
                    mb: 0.5
                  }}
                >
                  1
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#FE964A",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  Number of Senior Pastor
                </Typography>
              </Stack>
            </CardContent>
          </Card> */}

            {/* Associate Pastor Card */}
            <Card
              sx={{
                flex: 1,
                backgroundColor: "#E5EDF9",
                border: "none",
                borderRadius: "16px",
                boxShadow: "none"
              }}
              elevation={0}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#2F78EE",
                      fontSize: "2rem",
                      mb: 0.5
                    }}
                  >
                    {numberOfAssociatePastor}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#2F78EE",
                      fontWeight: 500,
                      fontSize: "0.875rem"
                    }}
                  >
                    {t("churches.profile.numberOfAssociatePastors")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* Staff Members Card */}
            <Card
              sx={{
                flex: 1,
                backgroundColor: "#F0EDF7",
                border: "none",
                borderRadius: "16px",
                boxShadow: "none"
              }}
              elevation={0}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#7F56D9",
                      fontSize: "2rem",
                      mb: 0.5
                    }}
                  >
                    {numberOfStaffMembers}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#7F56D9",
                      fontWeight: 500,
                      fontSize: "0.875rem"
                    }}
                  >
                    {t("churches.profile.numberOfStaffMembers")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* Leadership Team Card */}
            <Card
              sx={{
                flex: 1,
                backgroundColor: "#E5F5F6",
                border: "none",
                borderRadius: "16px",
                boxShadow: "none"
              }}
              elevation={0}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#00B0B9",
                      fontSize: "2rem",
                      mb: 0.5
                    }}
                  >
                    {numberOfLeadershipTeam}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#00B0B9",
                      fontWeight: 500,
                      fontSize: "0.875rem"
                    }}
                  >
                    {t("churches.profile.numberOfLeadershipTeam")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {/* Senior Pastor */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header with title and dedicated buttons */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#1a1a1a" }}
              >
                {t("churches.profile.seniorPastor")}
              </Typography>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                {isEditMode ? (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<X />}
                      onClick={handleEditToggle}
                      disabled={
                        leaderCreateMutation.isLoading ||
                        leaderUpdateMutation.isLoading
                      }
                    >
                      {t("churches.profile.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<PencilSimpleLine />}
                      disabled={
                        leaderCreateMutation.isLoading ||
                        leaderUpdateMutation.isLoading
                      }
                    >
                      {t("churches.profile.saveChanges")}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<PencilSimpleLine />}
                    onClick={handleEditToggle}
                  >
                    {t("members.profile.EditProfile")}
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Profile content */}
            <Stack direction="row" spacing={4}>
              {/* Avatar Section */}
              <Stack alignItems="center" sx={{ minWidth: 300 }}>
                <Box
                  sx={{
                    width: 320,
                    height: 410,
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    backgroundImage: seniorPastorSelectedFile
                      ? `url(${URL.createObjectURL(seniorPastorSelectedFile)})`
                      : church?.leaders?.senior_pastor?.[0]?.profile_picture
                      ? `url(${IMAGE_URL}/${church?.leaders?.senior_pastor?.[0]?.profile_picture})`
                      : `url(${ProfilePicturePlaceholder})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    color: "white"
                  }}
                >
                  <Box
                    sx={{
                      background:
                        "linear-gradient(to bottom, transparent, rgba(0,0,0,0.1))",
                      p: 2,
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      borderBottomLeftRadius: "16px",
                      borderBottomRightRadius: "16px"
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600
                      }}
                    >
                      {church?.leaders?.senior_pastor?.[0]?.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {church?.leaders?.senior_pastor?.[0]?.first_name}{" "}
                      {church?.leaders?.senior_pastor?.[0]?.last_name}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              {/* Form fields */}
              <Stack spacing={3} sx={{ flex: 1 }}>
                <Stack alignItems="flex-start">
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "25px",
                      px: 3,
                      py: 1,
                      fontSize: "0.875rem",
                      textTransform: "none"
                    }}
                    disabled={!isEditMode}
                    onClick={openSeniorPastorFileDialog}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2">
                        {t("addMembers.step1.UploadPhoto")}
                      </Typography>
                      <ArrowLineUpIcon />
                    </Stack>
                  </Button>
                  <Typography
                    variant="caption"
                    sx={{ color: "#9ca3af", mt: 1, textAlign: "center" }}
                  >
                    {t("churches.profile.recommendedPortraitPhoto")}
                  </Typography>
                </Stack>

                {/* Title field */}
                <Select
                  label={t("addMembers.step1.Title")}
                  name="senior_pastor_title"
                  options={titleOptions}
                  register={register}
                  error={!!errors.senior_pastor_title?.message}
                  helperText={errors.senior_pastor_title?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />

                {/* Name fields row */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("addMembers.step3.FirstName")}
                    name="senior_pastor_first_name"
                    register={register}
                    error={!!errors.senior_pastor_first_name?.message}
                    helperText={errors.senior_pastor_first_name?.message}
                    disabled={!isEditMode}
                    neutral={1}
                  />
                  <TextField
                    label={t("addMembers.step3.LastName")}
                    name="senior_pastor_last_name"
                    register={register}
                    error={!!errors.senior_pastor_last_name?.message}
                    helperText={errors.senior_pastor_last_name?.message}
                    disabled={!isEditMode}
                    neutral={1}
                  />
                </Stack>

                {/* Email and Phone row */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t("form.common.email")}
                    name="senior_pastor_email"
                    type="email"
                    register={register}
                    error={!!errors.senior_pastor_email?.message}
                    helperText={errors.senior_pastor_email?.message}
                    disabled={!isEditMode}
                    neutral={1}
                  />
                  <TextField
                    label={t("form.common.phone")}
                    name="senior_pastor_phone"
                    register={register}
                    error={!!errors.senior_pastor_phone?.message}
                    helperText={errors.senior_pastor_phone?.message}
                    disabled={!isEditMode}
                    neutral={1}
                  />
                </Stack>

                {/* Started Date field */}
                <TextField
                  label={t("churches.createChurch.startedDate")}
                  name="senior_pastor_started_date"
                  type="date"
                  register={register}
                  error={!!errors.senior_pastor_started_date?.message}
                  helperText={errors.senior_pastor_started_date?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Leaders Profile */}
        {!isDenominationChurch && (
          <Card
            sx={{
              borderRadius: "20px",
              border: "1px solid #e9eaec"
            }}
            elevation={0}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header with title and add button */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1a1a1a" }}
                >
                  {t("churches.profile.leadersProfile")}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Plus />}
                  onClick={handleAddLeader}
                >
                  {t("churches.profile.addLeader")}
                </Button>
              </Stack>

              {/* Leaders Grid */}
              {(leaders?.leader?.length > 0 || showAddLeaderForm) && (
                <Grid container spacing={3}>
                  {leaders?.leader?.map(leader => (
                    <Grid item xs={12} md={4} key={leader.id}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #e5e7eb",
                          textAlign: "center",
                          p: 2
                        }}
                        elevation={0}
                      >
                        {editingLeaderId === leader.id ? (
                          // Edit Mode
                          <>
                            {/* Action buttons for edit mode */}
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              sx={{ mb: 2 }}
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={e => {
                                  e.preventDefault();
                                  handleSubmitEditLeader(
                                    handleSaveEditLeader
                                  )();
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "white",
                                  border: "1px solid #39bf86",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                <Check size={16} color="#39bf86" />
                              </Box>
                              <Box
                                component="button"
                                type="button"
                                onClick={handleCancelEditLeader}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                <X size={16} />
                              </Box>
                            </Stack>

                            {/* Avatar with upload for edit mode */}
                            <Box
                              sx={{
                                width: 250,
                                height: 250,
                                borderRadius: "50%",
                                mx: "auto",
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#cbcaca",
                                backgroundImage: editLeaderSelectedFile
                                  ? `url(${URL.createObjectURL(
                                      editLeaderSelectedFile
                                    )})`
                                  : leader.profile_picture
                                  ? `url(${IMAGE_URL}/${leader.profile_picture})`
                                  : `url(${ProfilePicturePlaceholder})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center"
                              }}
                            />

                            {/* Upload Profile Button */}
                            <Button
                              variant="outlined"
                              sx={{
                                borderRadius: "25px",
                                px: 3,
                                py: 1,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                mb: 2
                              }}
                              onClick={openEditLeaderFileDialog}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography variant="body2">
                                  {t("addMembers.step1.UploadPhoto")}
                                </Typography>
                                <ArrowLineUpIcon />
                              </Stack>
                            </Button>

                            {/* Form fields for edit mode */}
                            <Stack spacing={2} sx={{ textAlign: "left" }}>
                              <Select
                                label={t("addMembers.step1.Title")}
                                name="edit_leader_title"
                                options={titleOptions}
                                register={registerEditLeader}
                                error={
                                  !!editLeaderErrors.edit_leader_title?.message
                                }
                                helperText={
                                  editLeaderErrors.edit_leader_title?.message
                                }
                                placeholder={t("churches.profile.selectTitle")}
                                neutral={1}
                              />
                              <TextField
                                label={t("form.common.fullName")}
                                name="edit_leader_full_name"
                                register={registerEditLeader}
                                error={
                                  !!editLeaderErrors.edit_leader_full_name
                                    ?.message
                                }
                                helperText={
                                  editLeaderErrors.edit_leader_full_name
                                    ?.message
                                }
                                neutral={1}
                              />
                              <PhoneInputField
                                label={t("form.common.phone")}
                                variant="filled"
                                placeholder="+251 - XXXXXXXXX"
                                value={watchEditLeader("edit_leader_phone")}
                                onChange={value =>
                                  setEditLeaderValue("edit_leader_phone", value)
                                }
                                error={
                                  !!editLeaderErrors.edit_leader_phone?.message
                                }
                                helperText={
                                  editLeaderErrors.edit_leader_phone?.message
                                }
                                neutral={1}
                              />
                              <TextField
                                label={t("form.common.email")}
                                name="edit_leader_email"
                                type="email"
                                register={registerEditLeader}
                                error={
                                  !!editLeaderErrors.edit_leader_email?.message
                                }
                                helperText={
                                  editLeaderErrors.edit_leader_email?.message
                                }
                                neutral={1}
                              />
                            </Stack>
                          </>
                        ) : (
                          // View Mode
                          <>
                            {/* Action buttons for view mode */}
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              sx={{ mb: 2 }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                onClick={() => handleEditLeader(leader)}
                              >
                                <PencilSimpleLine size={16} color="#6b7280" />
                              </Box>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                onClick={() => handleDeleteLeader(leader.id)}
                              >
                                <Trash size={16} color="#ef4444" />
                              </Box>
                            </Stack>

                            {/* Avatar for view mode */}
                            <Box
                              sx={{
                                width: 250,
                                height: 250,
                                borderRadius: "50%",
                                overflow: "hidden",
                                mx: "auto",
                                mb: 2,
                                backgroundImage: leader.profile_picture
                                  ? `url(${IMAGE_URL}/${leader.profile_picture})`
                                  : `url(${ProfilePicturePlaceholder})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center"
                              }}
                            />

                            {/* Leader info for view mode */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#22d3ee",
                                fontSize: "0.875rem",
                                mb: 0.5
                              }}
                            >
                              {leader.title}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: "#1a1a1a", mb: 1 }}
                            >
                              {leader.first_name} {leader.last_name}
                            </Typography>

                            {/* Contact info for view mode */}
                            <Stack spacing={1} alignItems="center">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Phone size={16} color="#6b7280" />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#6b7280" }}
                                >
                                  {leader.phone_number}
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Envelope size={16} color="#6b7280" />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#6b7280" }}
                                >
                                  {leader.email}
                                </Typography>
                              </Stack>
                            </Stack>
                          </>
                        )}
                      </Card>
                    </Grid>
                  ))}

                  {/* Add Leader Form Card */}
                  {showAddLeaderForm && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #e5e7eb",
                          textAlign: "center",
                          p: 2
                        }}
                        elevation={0}
                      >
                        {/* Action buttons */}
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            component="button"
                            type="submit"
                            onClick={e => {
                              e.preventDefault();
                              handleSubmitNewLeader(handleSaveLeader)();
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "white",
                              border: "1px solid #39bf86",
                              borderRadius: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            <Check size={16} color="#39bf86" />
                          </Box>
                          <Box
                            component="button"
                            type="button"
                            onClick={handleCancelAddLeader}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "white",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            <X size={16} />
                          </Box>
                        </Stack>

                        {/* Avatar placeholder with upload button */}
                        <Box
                          sx={{
                            width: 250,
                            height: 250,
                            borderRadius: "50%",
                            mx: "auto",
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#cbcaca",
                            backgroundImage: newLeaderSelectedFile
                              ? `url(${URL.createObjectURL(
                                  newLeaderSelectedFile
                                )})`
                              : `url(${ProfilePicturePlaceholder})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center"
                          }}
                        />

                        {/* Upload Profile Button */}
                        <Button
                          variant="outlined"
                          sx={{
                            borderRadius: "25px",
                            px: 3,
                            py: 1,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            mb: 2
                          }}
                          onClick={openNewLeaderFileDialog}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body2">
                              {t("addMembers.step1.UploadPhoto")}
                            </Typography>
                            <ArrowLineUpIcon />
                          </Stack>
                        </Button>

                        {/* Form fields */}
                        <Stack spacing={2} sx={{ textAlign: "left" }}>
                          <Select
                            label={t("addMembers.step1.Title")}
                            name="leader_title"
                            options={titleOptions}
                            register={registerNewLeader}
                            error={!!newLeaderErrors.leader_title?.message}
                            helperText={newLeaderErrors.leader_title?.message}
                            placeholder={t("churches.profile.selectTitle")}
                            neutral={1}
                          />
                          <TextField
                            label={t("form.common.fullName")}
                            name="leader_full_name"
                            register={registerNewLeader}
                            error={!!newLeaderErrors.leader_full_name?.message}
                            helperText={
                              newLeaderErrors.leader_full_name?.message
                            }
                            neutral={1}
                          />
                          <PhoneInputField
                            label={t("form.common.phone")}
                            variant="filled"
                            placeholder="+251 - XXXXXXXXX"
                            value={watchNewLeader("leader_phone")}
                            onChange={value =>
                              setNewLeaderValue("leader_phone", value)
                            }
                            error={!!newLeaderErrors.leader_phone?.message}
                            helperText={newLeaderErrors.leader_phone?.message}
                            neutral={1}
                          />
                          <TextField
                            label={t("form.common.email")}
                            name="leader_email"
                            type="email"
                            register={registerNewLeader}
                            error={!!newLeaderErrors.leader_email?.message}
                            helperText={newLeaderErrors.leader_email?.message}
                            neutral={1}
                          />
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        )}

        {/* Mutation Error Display */}
        <MutationError
          error={leaderCreateMutation.error || leaderUpdateMutation.error}
        />
      </Stack>
    </Box>
  );
};
